import { Controller } from "@hotwired/stimulus"
import { contrast, mix, parseHex, toHex } from "../lib/color"

const WHITE: [number, number, number] = [255, 255, 255]
const BLACK: [number, number, number] = [0, 0, 0]
const AA = 4.5
const PASS_CLASSES = ["border-success-line", "bg-success-soft", "text-success-fg"]
const FAIL_CLASSES = ["border-danger-line", "bg-danger-soft", "text-danger-fg"]

export default class extends Controller<HTMLElement> {
  static targets = ["hex", "picker", "contrast", "font", "preview"]

  declare readonly hexTargets: HTMLInputElement[]
  declare readonly pickerTargets: HTMLInputElement[]
  declare readonly contrastTargets: HTMLElement[]
  declare readonly fontTargets: HTMLSelectElement[]
  declare readonly previewTargets: HTMLImageElement[]

  connect(): void {
    this.hexTargets.forEach(input => this.apply(input.dataset.key))
    this.fontTargets.forEach(select => this.applyFont(select))
    this.refreshContrast()
  }

  disconnect(): void {
    const style = document.documentElement.style
    this.hexTargets.forEach(input => style.removeProperty(input.dataset.variable))
    style.removeProperty("--font-body")
    style.removeProperty("--font-heading")
  }

  pick(event: Event): void {
    const picker = event.currentTarget as HTMLInputElement
    this.hexFor(picker.dataset.key).value = picker.value
    this.changed(picker.dataset.key)
  }

  type(event: Event): void {
    const input = event.currentTarget as HTMLInputElement
    const rgb = parseHex(input.value)
    if (rgb !== null) this.pickerFor(input.dataset.key).value = toHex(rgb)
    this.changed(input.dataset.key)
  }

  reset(event: Event): void {
    const key = (event.currentTarget as HTMLElement).dataset.key
    const input = this.hexFor(key)
    input.value = ""
    this.pickerFor(key).value = input.dataset.default
    this.changed(key)
  }

  preview(event: CustomEvent<{ slot: string; url: string }>): void {
    this.previewTargets.filter(image => image.dataset.slot === event.detail.slot).forEach(image => {
      image.src = event.detail.url
      image.hidden = false
    })
  }

  font(event: Event): void {
    this.applyFont(event.currentTarget as HTMLSelectElement)
  }

  private changed(key: string): void {
    this.apply(key)
    if (key === "accent") ["accent_strong", "accent_soft"].forEach(derived => this.apply(derived))
    this.refreshContrast()
  }

  private apply(key: string): void {
    const input = this.hexFor(key)
    const value = this.explicit(key) ?? this.derived(key)
    if (value === null) document.documentElement.style.removeProperty(input.dataset.variable)
    else document.documentElement.style.setProperty(input.dataset.variable, value)
  }

  private explicit(key: string): string | null {
    const rgb = parseHex(this.hexFor(key)?.value ?? "")
    return rgb === null ? null : toHex(rgb)
  }

  private derived(key: string): string | null {
    const accent = parseHex(this.hexFor("accent")?.value ?? "")
    if (accent === null) return null
    if (key === "accent_strong") return toHex(mix(accent, BLACK, 0.15))
    if (key === "accent_soft") return toHex(mix(accent, WHITE, 0.88))
    return null
  }

  private effective(key: string): string {
    return this.explicit(key) ?? this.derived(key) ?? this.hexFor(key).dataset.default
  }

  private refreshContrast(): void {
    this.contrastTargets.forEach(badge => {
      const ratio = contrast(parseHex(this.effective(badge.dataset.fg)), parseHex(this.effective(badge.dataset.bg)))
      const passes = ratio >= AA
      badge.textContent = `${ratio.toFixed(1)}:1 ${passes ? "AA" : "low"}`
      badge.classList.remove(...PASS_CLASSES, ...FAIL_CLASSES)
      badge.classList.add(...(passes ? PASS_CLASSES : FAIL_CLASSES))
      badge.title = passes ? "Meets WCAG AA for normal text" : "Below WCAG AA (4.5:1): hard to read for some people"
    })
  }

  private applyFont(select: HTMLSelectElement): void {
    const stack = select.selectedOptions[0]?.dataset.stack
    const variable = select.dataset.variable
    if (stack === undefined || stack === "") document.documentElement.style.removeProperty(variable)
    else document.documentElement.style.setProperty(variable, stack)
  }

  private hexFor(key: string): HTMLInputElement {
    return this.hexTargets.find(input => input.dataset.key === key)
  }

  private pickerFor(key: string): HTMLInputElement {
    return this.pickerTargets.find(input => input.dataset.key === key)
  }
}
